import {
  downloadBasketsAccountingCsv,
  downloadPaymentsCsv,
  downloadPeriodAccountingKpiXlsx,
  downloadPortVieuxVatStatementXlsx,
  downloadWorksConcilHistoriesCsv,
  useAccountingKpis,
  useAccountingWarnings,
} from '@gozoki/api'
import { FLAGS } from '@gozoki/feature-flags'
import { frenchFormat, type Right } from '@gozoki/tools'
import { Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowDownToLine } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import type { PermissionTuple } from '~/utils/ability'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'
import Page from '../../components/Page'
import { DashboardCard } from '../../utils/dashboard/dashboardCards'
import { fonts } from '../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/accounting')({
  staticData: {
    permission: ['read', 'accounting.accounting.figures'],
  },
  component: AccountingPage,
})
withRoutePermissions(Route)

const EXPORT_FIGURES: PermissionTuple = ['export', 'accounting.accounting.figures']
const EXPORT_BALANCE: PermissionTuple = ['export', 'accounting.accounting.balance']
const EXPORT_PAYMENTS: PermissionTuple = ['export', 'accounting.accounting.payments']
const EXPORT_CE_HISTORY: PermissionTuple = ['export', 'accounting.accounting.works-council-history']
const READ_WARNINGS: PermissionTuple = ['read', 'accounting.accounting.warnings']

/**
 * L'écran entier était réservé à `ACCOUNTING_READER` : tant que le flag `permission-v2` est OFF,
 * les tuples v2 passent tous et ce bitmask reste la seule garde des affordances.
 */
const LEGACY_RIGHTS: Right[] = ['ACCOUNTING_READER']

const yersterday = DateTime.now().minus({ days: 1 })
const aWeekAgo = DateTime.now().minus({ days: 7 })
const lastMonth = DateTime.now().minus({ months: 1 }).startOf('month')

function AccountingPage() {
  const [currentDate, setCurrentDate] = useState<DateTime>(yersterday)

  const [currentStartDate, setCurrentStartDate] = useState<DateTime>(aWeekAgo)
  const [currentEndDate, setCurrentEndDate] = useState<DateTime>(yersterday)
  const [statementMonth, setStatementMonth] = useState<DateTime>(lastMonth)

  const [isLoading, setIsLoading] = useState(false)

  const currentFormatedDate = useMemo(() => currentDate.toFormat('yyyy-MM-dd'), [currentDate])
  const currentFormatedStartDate = useMemo(
    () => currentStartDate.toFormat('yyyy-MM-dd'),
    [currentStartDate],
  )
  const currentFormatedEndDate = useMemo(
    () => currentEndDate.toFormat('yyyy-MM-dd'),
    [currentEndDate],
  )

  const { data: accounting } = useAccountingKpis(currentFormatedDate)
  const { data: warnings } = useAccountingWarnings()

  const canReadWarnings = useMigratedRight(READ_WARNINGS, LEGACY_RIGHTS)

  const comptaExportPanierEnabled = useFeatureFlag(FLAGS.comptaExportPanier)

  const { mutate: downloadAuditFile } = useMutation({
    mutationFn: downloadPaymentsCsv,
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading payments file')
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `paiements-${currentFormatedStartDate}_${currentFormatedEndDate}.zip`,
      )
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
    },
  })

  const { mutate: downloadBasketsExportFile } = useMutation({
    mutationFn: downloadBasketsAccountingCsv,
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading baskets accounting export file')
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
      const link = document.createElement('a')
      link.href = url
      const start = currentStartDate.toFormat('dd_MM_yyyy')
      const end = currentEndDate.toFormat('dd_MM_yyyy')
      link.setAttribute('download', `Export panier_comptabilité_du_${start}_au_${end}.csv`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
      URL.revokeObjectURL(url)
    },
  })

  const { mutate: downloadPeriodAccountingKpiFiles } = useMutation({
    mutationFn: ({
      dateStart,
      dateEnd,
    }: {
      dateStart: string
      dateEnd: string
      isSingleDay?: boolean
    }) => downloadPeriodAccountingKpiXlsx({ dateStart, dateEnd }),
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading donnees-comptabilite file')
    },
    onSuccess: (response, variables) => {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
      const link = document.createElement('a')
      link.href = url
      const fileName = variables.isSingleDay
        ? `donnees-comptabilite_${variables.dateStart}.zip`
        : `donnees-comptabilite_${variables.dateStart}_${variables.dateEnd}.zip`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
      URL.revokeObjectURL(url)
    },
  })

  const { mutate: downloadPortVieuxStatement } = useMutation({
    mutationFn: (month: string) => downloadPortVieuxVatStatementXlsx(month),
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading Port Vieux VAT statement file')
    },
    onSuccess: (response, month) => {
      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `releve-ca-port-vieux-${month}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
      URL.revokeObjectURL(url)
    },
  })

  const { mutate: downloadWorksConcilsHistoryFile } = useMutation({
    mutationFn: (date: string) => downloadWorksConcilHistoriesCsv(date),
    onError: () => {
      setIsLoading(false)
      console.error('Error while downloading works concils histories file')
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `CE-historique_${currentFormatedDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      setIsLoading(false)
    },
  })

  const {
    internalNegoceHt,
    internalNegoceTva,
    externalNegoceHt,
    externalNegoceTva,
    sushisTtc,
    amountCb,
    amountConecs,
    unPaidAmount,
    promoAmountHt,
    promoAmountTva,
  } = accounting?.main ?? {
    amountCb: 0,
    amountConecs: 0,
    externalNegoceHt: 0,
    externalNegoceTva: 0,
    internalNegoceHt: 0,
    internalNegoceTva: 0,
    promoAmountHt: 0,
    promoAmountTva: 0,
    sushisTtc: 0,
    unPaidAmount: 0,
  }

  const handleDownload = useCallback(async () => {
    setIsLoading(true)
    downloadPeriodAccountingKpiFiles({
      dateEnd: currentFormatedDate,
      dateStart: currentFormatedDate,
      isSingleDay: true,
    })
  }, [currentFormatedDate, downloadPeriodAccountingKpiFiles])

  const handlePeriodDownload = useCallback(async () => {
    setIsLoading(true)
    downloadPeriodAccountingKpiFiles({
      dateEnd: currentFormatedEndDate,
      dateStart: currentFormatedStartDate,
      isSingleDay: false,
    })
  }, [currentFormatedStartDate, currentFormatedEndDate, downloadPeriodAccountingKpiFiles])

  const handleDownloadPortVieuxStatement = useCallback(() => {
    setIsLoading(true)
    downloadPortVieuxStatement(statementMonth.toFormat('yyyy-MM'))
  }, [statementMonth, downloadPortVieuxStatement])

  const handleDownloadPaymentsFile = useCallback(() => {
    setIsLoading(true)
    const startIsoDate = currentStartDate.toISODate()
    const endIsoDate = currentEndDate.toISODate()
    downloadAuditFile({ dateEnd: endIsoDate ?? '', dateStart: startIsoDate ?? '' })
  }, [currentStartDate, currentEndDate, downloadAuditFile])

  const handleDownloadBasketsExport = useCallback(() => {
    setIsLoading(true)
    const startIsoDate = currentStartDate.toISODate()
    const endIsoDate = currentEndDate.toISODate()
    downloadBasketsExportFile({ dateEnd: endIsoDate ?? '', dateStart: startIsoDate ?? '' })
  }, [currentStartDate, currentEndDate, downloadBasketsExportFile])

  return (
    <Page sx={fonts.roboto}>
      <div style={{ display: 'flex', flexDirection: 'row', padding: 20 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Comptabilité
        </Typography>
        <AuthorizedButton
          permission={EXPORT_FIGURES}
          legacyRights={LEGACY_RIGHTS}
          style={{ marginRight: 40, marginTop: 8, gap: 6 }}
          variant="contained"
          disabled={isLoading}
          onClick={handleDownload}
        >
          <ArrowDownToLine strokeWidth={2} />
          Exporter le jour sélectionné
        </AuthorizedButton>
        <DatePicker
          value={currentDate}
          onChange={(date: DateTime | null) => setCurrentDate(date ?? yersterday)}
          format="dd/MM/yyyy"
        />
      </div>
      <div style={{ border: '3px solid white', padding: 10 }}>
        {/* <Typography style={fonts.pageSubtitle} flexGrow={1}>
                    Comptabilité principale
                </Typography> */}
        <div style={{ display: 'flex' }}>
          <DashboardCard
            label="Vente Négoce interne HT"
            bigValue={`${frenchFormat(internalNegoceHt)}\u00A0€ HT`}
            smallValue={`TVA : ${frenchFormat(internalNegoceTva)}\u00A0€`}
            orange
          />
          <DashboardCard
            label="Vente négoce externe HT"
            bigValue={`${frenchFormat(externalNegoceHt)}\u00A0€ HT`}
            smallValue={`TVA : ${frenchFormat(externalNegoceTva)}\u00A0€`}
          />
          <DashboardCard label="Vente Sushis TTC" bigValue={`${frenchFormat(sushisTtc)}\u00A0€`} />
        </div>
        <div style={{ display: 'flex' }}>
          <DashboardCard label="Ventes CB" bigValue={`${frenchFormat(amountCb)}\u00A0€`} />
          <DashboardCard label="Ventes Conecs" bigValue={`${frenchFormat(amountConecs)}\u00A0€`} />
          <DashboardCard
            label="Impayés clients TTC"
            bigValue={`${frenchFormat(unPaidAmount)}\u00A0€`}
          />
          <DashboardCard
            label="Montant promotions HT"
            bigValue={`${frenchFormat(promoAmountHt)}\u00A0€`}
            smallValue={`TVA : ${frenchFormat(promoAmountTva)}\u00A0€`}
          />
        </div>
      </div>
      <div style={{ padding: 30 }} />
      <div
        style={{
          border: '3px solid white',
          padding: 10,
        }}
      >
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          Exporter sur une période donnée
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            paddingTop: 20,
          }}
        >
          <div style={{ padding: 10 }}>
            <Typography style={fonts.inputSubtitle} flexGrow={1}>
              Date de début
            </Typography>
            <DatePicker
              value={currentStartDate}
              onChange={(date: DateTime | null) => setCurrentStartDate(date ?? yersterday)}
              format="dd/MM/yyyy"
            />
          </div>
          <div style={{ padding: 10 }}>
            <Typography style={fonts.inputSubtitle} flexGrow={1}>
              Date de fin
            </Typography>
            <DatePicker
              value={currentEndDate}
              onChange={(date: DateTime | null) => setCurrentEndDate(date ?? yersterday)}
              format="dd/MM/yyyy"
            />
          </div>
          <AuthorizedButton
            permission={EXPORT_BALANCE}
            legacyRights={LEGACY_RIGHTS}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="secondary"
            disabled={isLoading}
            onClick={handlePeriodDownload}
          >
            <ArrowDownToLine strokeWidth={2} />
            Exporter sur le bilan comptable
          </AuthorizedButton>
          <AuthorizedButton
            permission={EXPORT_PAYMENTS}
            legacyRights={LEGACY_RIGHTS}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="info"
            onClick={handleDownloadPaymentsFile}
            disabled={isLoading}
          >
            <ArrowDownToLine strokeWidth={2} />
            Exporter les paiements
          </AuthorizedButton>
          <AuthorizedButton
            permission={EXPORT_CE_HISTORY}
            legacyRights={LEGACY_RIGHTS}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="info"
            onClick={() => downloadWorksConcilsHistoryFile(currentFormatedDate)}
            disabled={isLoading}
          >
            <ArrowDownToLine strokeWidth={2} />
            Exporter les historiques de CE
          </AuthorizedButton>
          {comptaExportPanierEnabled && (
            <AuthorizedButton
              permission={EXPORT_PAYMENTS}
              legacyRights={LEGACY_RIGHTS}
              style={{ marginLeft: 40, marginTop: 40 }}
              variant="outlined"
              color="info"
              onClick={handleDownloadBasketsExport}
              disabled={isLoading}
            >
              <ArrowDownToLine strokeWidth={2} />
              Exports panier
            </AuthorizedButton>
          )}
        </div>
      </div>
      <div
        style={{
          border: '3px solid white',
          marginTop: 20,
          padding: 10,
        }}
      >
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          Relevé TVA mensuel — Port Vieux
        </Typography>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            paddingTop: 20,
          }}
        >
          <div style={{ padding: 10 }}>
            <Typography style={fonts.inputSubtitle} flexGrow={1}>
              Mois
            </Typography>
            <DatePicker
              value={statementMonth}
              onChange={(date: DateTime | null) => setStatementMonth(date ?? lastMonth)}
              views={['year', 'month']}
              openTo="month"
              format="MM/yyyy"
            />
          </div>
          <AuthorizedButton
            permission={EXPORT_FIGURES}
            legacyRights={LEGACY_RIGHTS}
            style={{ marginLeft: 40, marginTop: 40 }}
            variant="outlined"
            color="secondary"
            disabled={isLoading}
            onClick={handleDownloadPortVieuxStatement}
          >
            <ArrowDownToLine strokeWidth={2} />
            Télécharger le relevé (CA par taux de TVA)
          </AuthorizedButton>
        </div>
      </div>
      <div
        style={{
          border: '3px solid white',
          marginTop: 20,
          padding: 10,
        }}
      >
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          Avertissements
        </Typography>
        <div style={{ display: 'flex', flexDirection: 'column', padding: 20 }}>
          {canReadWarnings ? (
            warnings?.map((warning) => (
              <Typography style={fonts.inputSubtitle} key={warning.date}>
                {warning.date} : différence de&nbsp;&nbsp;
                {frenchFormat(warning.difference)}€
              </Typography>
            ))
          ) : (
            <Typography style={fonts.inputSubtitle}>
              Vous n'êtes pas autorisé à consulter les avertissements comptables
            </Typography>
          )}
        </div>
      </div>
    </Page>
  )
}
